import { E2EElement, newE2EPage } from "@stencil/core/testing";
import { testValidHtml } from "../../../test-utils/html-validate";
import {
    formTemplate,
    formWithMultipleElements,
    greenMarketFormFirstStep,
    greenMarketFormSecondStep,
    templateWithStaticFilter
} from "./form-test-templates";
import { getValidationAttributes } from "./utils";

describe("form", () => {
    it("renders the content of the slot", async () => {
        const page = await newE2EPage();
        await page.setContent(`<ux-form>
            <ux-input-field input-id="1"></ux-input-field>
        </ux-form>`);
        await page.waitForChanges();
        const uxForm = await page.find("ux-form");
        const uxInputField = await page.find("ux-input-field");
        expect(uxForm).toHaveClass("hydrated");
        expect(uxInputField).toHaveClass("hydrated");
    });

    it("matches to shapshot", async () => {
        const page = await newE2EPage();
        await page.setContent(formTemplate({ novalidate: false }));
        await page.waitForChanges();
        const button = await page.find("#submit");
        button.click();
        await page.waitForChanges();
        const el = await page.find("ux-form");
        expect(el).toMatchSnapshot();
    });

    it("should handle the validation process correctly", async () => {
        const page = await newE2EPage();
        await page.setContent(formTemplate({ novalidate: false }));
        await page.waitForChanges();
        const submitEvent = await page.spyOnEvent("submit");
        const eventHandler = await page.spyOnEvent("uxFormValidated");
        const submitButton = await page.find("ux-form button[type='submit']");
        await submitButton.click();
        expect(eventHandler).toHaveReceivedEventDetail({
            isValid: false,
            formData: {
                test: {
                    values: [""]
                },
                email: {
                    values: ["invalidEmail"]
                }
            },
            serializedFormData: "test=&email=invalidEmail"
        });
        expect(submitEvent).not.toHaveReceivedEvent();
        const uxInputText = await page.find("ux-input-field");
        const inputText = await uxInputText.find("input");
        await inputText.focus();
        uxInputText.setAttribute("value", "test");
        const uxInputEmail = await page.find("ux-input-field:nth-of-type(2)");
        const inputEmail = await uxInputEmail.find("input");
        await inputEmail.focus();
        uxInputEmail.setAttribute("value", "test@test.com");
        await page.waitForChanges();
        await submitButton.click();
        expect(eventHandler).toHaveReceivedEventDetail({
            isValid: true,
            formData: {
                test: {
                    values: ["test"]
                },
                email: {
                    values: ["test@test.com"]
                }
            },
            serializedFormData: "test=test&email=test%40test.com"
        });
        expect(submitEvent).toHaveReceivedEvent();
    });

    it("skips validation with novalidate attribute set on form", async () => {
        const page = await newE2EPage();
        await page.setContent(formTemplate({ novalidate: "form" }));
        await page.waitForChanges();
        const submitEvent = await page.spyOnEvent("submit");
        const eventHandler = await page.spyOnEvent("uxFormValidated");
        const submit = await page.find("*[type='submit']");
        await submit.click();
        expect(eventHandler).not.toHaveReceivedEvent();
        expect(submitEvent).toHaveReceivedEvent();
    });

    it("handles invalid fields hidden with ux-static-filter", async () => {
        const page = await newE2EPage();
        await page.setContent(templateWithStaticFilter());
        await page.waitForChanges();
        const uxDropdown = await page.find(".ux-dropdown");
        const submitEvent = await page.spyOnEvent("submit");
        const eventHandler = await page.spyOnEvent("uxFormValidated");
        const submit = await page.find("*[type='submit']");
        await submit.click();
        await page.waitForChanges();
        expect(eventHandler).toHaveReceivedEventDetail({
            isValid: false,
            formData: {
                email: {
                    values: ["invalidEmail"]
                },
                test: {
                    values: [""]
                },
                machine: {
                    values: ["", "", ""]
                }
            },
            serializedFormData: "machine=&test=&email=invalidEmail"
        });
        expect(submitEvent).not.toHaveReceivedEvent();
        await uxDropdown.click();
        await page.waitForChanges();
        const radioButton = await uxDropdown.find("ux-radio-list-item[label='choice 3'][value='2']");
        await radioButton.click();
        const uxInputText = await page.find("ux-input-field");
        const inputText = await uxInputText.find("input");
        await inputText.focus();
        uxInputText.setAttribute("value", "test");
        await page.waitForChanges();
        await submit.click();
        await page.waitForChanges();
        expect(eventHandler).toHaveReceivedEventDetail({
            isValid: true,
            formData: {
                email: {
                    values: ["invalidEmail"]
                },
                test: {
                    values: ["test"]
                },
                machine: {
                    values: ["", "", "2"]
                }
            },
            serializedFormData: "machine=2&test=test"
        });
        expect(submitEvent).toHaveReceivedEvent();
    });

    it("validates correctly form with multiple elements", async () => {
        const page = await newE2EPage();
        await page.setContent(formWithMultipleElements());
        await page.waitForChanges();
        const submitEvent = await page.spyOnEvent("submit");
        const uxFormValidatedEvent = await page.spyOnEvent("uxFormValidated");
        const submit = await page.find("*[type='submit']");
        const uxInputField = await page.find("ux-input-field");
        const uxCheckbox = await page.find("ux-checkbox");
        const uxPhoneInput = await page.find("ux-phone-input");
        const uxTextareaField = await page.find("ux-textarea-field");
        const uxSelectLabel = await page.find("ux-select .ux-label__error-message");
        let uxInputFieldLabel = await page.find("ux-input-field .ux-label__error-message");
        let uxCheckboxLabel = await page.find("ux-checkbox .ux-label__error-message");
        let uxPhoneInputLabel = await page.find("ux-phone-input .ux-label__error-message");
        let uxTextareaFieldLabel = await page.find("ux-textarea-field .ux-label__error-message");
        expect(uxSelectLabel).toBeFalsy();
        expect(uxInputFieldLabel).toBeFalsy();
        expect(uxCheckboxLabel).toBeFalsy();
        expect(uxPhoneInputLabel).toBeFalsy();
        expect(uxTextareaFieldLabel).toBeFalsy();
        await submit.click();
        await page.waitForChanges();
        expect(uxFormValidatedEvent).toHaveReceivedEvent();
        expect(submitEvent).not.toHaveReceivedEvent();
        uxInputFieldLabel = await page.find("ux-input-field .ux-label__error-message");
        uxCheckboxLabel = await page.find("ux-checkbox .ux-label__error-message");
        uxPhoneInputLabel = await page.find("ux-phone-input .ux-label__error-message");
        uxTextareaFieldLabel = await page.find("ux-textarea-field .ux-label__error-message");
        expect(uxInputFieldLabel).toBeTruthy();
        expect(uxCheckboxLabel).toBeTruthy();
        expect(uxPhoneInputLabel).toBeTruthy();
        expect(uxTextareaFieldLabel).toBeTruthy();
        await page.waitForChanges();
        const textareaEl = await uxTextareaField.find("textarea");
        const inputEl = await uxInputField.find("input");
        const phoneInputEl = await uxPhoneInput.find("input");
        await inputEl.press("A");
        await uxCheckbox.click();
        await phoneInputEl.press("3");
        await phoneInputEl.press("4");
        await phoneInputEl.press("3");
        await phoneInputEl.press("4");
        await phoneInputEl.press("3");
        await textareaEl.type("A");
        uxInputFieldLabel = await page.find("ux-input-field .ux-label__error-message");
        uxCheckboxLabel = await page.find("ux-checkbox .ux-label__error-message");
        uxPhoneInputLabel = await page.find("ux-phone-input .ux-label__error-message");
        uxTextareaFieldLabel = await page.find("ux-textarea-field .ux-label__error-message");
        expect(uxInputFieldLabel).toBeFalsy();
        expect(uxCheckboxLabel).toBeFalsy();
        expect(uxPhoneInputLabel).toBeFalsy();
        expect(uxTextareaFieldLabel).toBeFalsy();
        await submit.click();
        expect(submitEvent).toHaveReceivedEvent();
    });

    it("passes validation test green market mock step 1", async () => {
        const page = await newE2EPage();
        await page.setContent(greenMarketFormFirstStep());
        await page.waitForChanges();
        const submitEvent = await page.spyOnEvent("submit");
        const valudatedEvent = await page.spyOnEvent("uxFormValidated");
        const submit = await page.find("*[type='submit']");
        await submit.click();
        await page.waitForChanges();
        expect(submitEvent).toHaveReceivedEvent();
        expect(valudatedEvent).toHaveReceivedEvent();
        expect(valudatedEvent).toHaveReceivedEventDetail({
            isValid: true,
            /* radio elements not wrapped in ux-form-group, ther values
            are not picked up the the formData custom object, but this
            is irrelevant, since the form is not handled with JS */
            formData: {},
            serializedFormData: "format=1&power=5&machine=9"
        });
    });

    it("passes validation test green market mock step 2 go back", async () => {
        const page = await newE2EPage();
        await page.setContent(greenMarketFormSecondStep());
        await page.waitForChanges();
        const submitEvent = await page.spyOnEvent("submit");
        const validatedEvent = await page.spyOnEvent("uxFormValidated");
        const submit = await page.find("*[formnovalidate]");
        await submit.click();
        expect(validatedEvent).not.toHaveReceivedEvent();
        expect(submitEvent).toHaveReceivedEvent();
    });

    it("passes validation test green market mock step 2 go next", async () => {
        const page = await newE2EPage();
        await page.setContent(greenMarketFormSecondStep());
        await page.waitForChanges();
        const submitEvent = await page.spyOnEvent("submit");
        const validatedEvent = await page.spyOnEvent("uxFormValidated");
        const submit = await page.find("button[type='submit']");
        const inp1 = await page.find("input[name='first-name-delivery']");
        const inp2 = await page.find("input[name='last-name-delivery']");
        const inp3 = await page.find("input[name='company-delivery']");
        const inp4 = await page.find("input[name='street-delivery']");
        const inp5 = await page.find("input[name='city-delivery']");
        const inp6 = await page.find("input[name='zip-delivery']");
        const notRequiredInp = await page.find("input[name='unit-delivery']");
        const inpPhone = await page.find("ux-phone-input[name='phone-delivery'] input.ux-input");
        const inpEmail = await page.find("input[name='email-delivery']");
        // const billingAddressLabel = await page.find("ux-radio-button[value='1'] label");
        // const deliveryAddressLabel = await page.find("ux-radio-button[value='2'] label");
        error([inp1, inp2, inp3, inp4, inp5, inp6, inpPhone, inpEmail], false);
        error([notRequiredInp], false);
        await submit.click();
        await page.waitForChanges();
        expect(validatedEvent).toHaveReceivedEvent();
        expect(submitEvent).not.toHaveReceivedEvent();
        error([inp1, inp2, inp3, inp4, inp5, inp6, inpPhone, inpEmail], true);
        error([notRequiredInp], false);
        // give a value to the required form elements
        await inp1.press("A");
        await inp2.press("A");
        await inp3.press("A");
        await inp4.press("A");
        await inp5.press("A");
        await inp6.press("2");
        await typeInPhoneInput(inpPhone);
        await typeInEmailInput(inpEmail);
        await page.waitForChanges();
        // check that input fields are error free after interaction
        error([inp1, inp2, inp3, inp4, inp5, inp6, inpPhone, inpEmail], false);
        error([notRequiredInp], false);
        await page.waitForChanges();
        // display the delivery address field
        await submit.click();
        await page.waitForChanges();
        expect(validatedEvent).toHaveReceivedEvent();
        expect(submitEvent).toHaveReceivedEvent();
    });

    it("skips validation with formnovalidate attribute set on submit field", async () => {
        const page = await newE2EPage();
        await page.setContent(formTemplate({ novalidate: "button" }));
        await page.waitForChanges();
        const submitEvent = await page.spyOnEvent("submit");
        const eventHandler = await page.spyOnEvent("uxFormValidated");
        const submit = await page.find("*[type='submit']");
        await submit.click();
        expect(eventHandler).not.toHaveReceivedEvent();
        expect(submitEvent).toHaveReceivedEvent();
    });

    it("should render valid html", async () => {
        await testValidHtml(`<ux-form>
            <form action="#" method="post">
                <ux-input-field input-id="1" label="username" name="username"></ux-input-field>
        </ux-form>`);
    });

    it("gets validation attributes", async () => {
        const page = await newE2EPage();
        await page.setContent(`
            <ux-input-field
                data-validation-length--min="3"
                data-validation-length--max="8"
                data-validation-value--min="4"
                data-validation-value--max="10"
                data-validation-regex--expression="^[A-Z]{1}[0-9]{2}[A-Z]{1}[0-9]{1}$"
                data-validation-regex--flag="i"
            ></ux-input-field>
        `);
        //@ts-ignore
        const el = (await page.find("ux-input-field")) as HTMLUxInputFieldElement;
        const { minLength, maxLength, minValue, maxValue, regexExpression, regexFlag } = getValidationAttributes({
            uxElement: el,
            dataLength: "data-validation-length",
            dataValue: "data-validation-value",
            dataRegex: "data-validation-regex"
        });
        expect(minLength).toBe(3);
        expect(maxLength).toBe(8);
        expect(minValue).toBe(4);
        expect(maxValue).toBe(10);
        expect(regexExpression).toBe("^[A-Z]{1}[0-9]{2}[A-Z]{1}[0-9]{1}$");
        expect(regexFlag).toBe("i");
    });
});

const error = (elements: E2EElement[], has: boolean) => {
    elements.forEach((element: E2EElement) => {
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        has ? expect(element).toEqualAttribute("aria-invalid", "true") : expect(element).toEqualAttribute("aria-invalid", "false");
    });
};

const typeInPhoneInput = async (phoneInput: E2EElement) => {
    await phoneInput.press("3");
    await phoneInput.press("4");
    await phoneInput.press("3");
    await phoneInput.press("4");
    await phoneInput.press("3");
};

const typeInEmailInput = async (emailInput: E2EElement) => {
    await emailInput.type("test@test.com");
};
